/**
 * Recurrence expansion — pure algorithmic helpers, shared across:
 *   - Supervision series (SupervisionSeries → Supervision rows)
 *   - Events series (event_series → events rows)
 *
 * Pass a `timezone` to preserve wall-clock intent across DST boundaries
 * ("1pm Eastern every Thursday" stays at 1pm even when the target date is
 * in a different DST regime than the anchor). Without a timezone, math is
 * naive UTC and drifts an hour across DST — legacy path, avoid.
 *
 * Format-agnostic: both supervision and events use the same expander
 * with their own enums shadowed to the same shape via SeriesPattern +
 * SeriesMonthlyMode.
 */

import moment from 'moment-timezone'

/**
 * Tzdata source is moment-timezone (bundled, updatable via `pnpm update`),
 * NOT Node's ICU. Node ICU tzdata trails IANA by months — BC's 2026-03-08
 * permanent-DST and AB's 2026-11-01 change aren't in Node 22 LTS as of
 * 2026-09, so date-fns-tz (which delegates to Intl → ICU) drifts an hour
 * for BC/AB dates past those cutovers. moment-timezone has both changes
 * baked in, so all our zone arithmetic below is correct today.
 */

export type RecurrencePattern = 'none' | 'weekly' | 'biweekly' | 'monthly'
export type MonthlyMode = 'date' | 'weekday'

export interface RecurrenceSpec {
  pattern: Exclude<RecurrencePattern, 'none'>
  count: number
  /** Only meaningful when `pattern === 'monthly'`. Defaults to 'date'. */
  monthlyMode?: MonthlyMode
}

/** Server-side cap for the ad-hoc "create N slots at once" path. */
export const MAX_RECURRENCE_COUNT = 26

/** Higher cap for supervisor-published series (Alli practicum = 30 weeks). */
export const MAX_SERIES_OCCURRENCE_COUNT = 60

/** Last day-of-month for the given UTC year/month (month is 0-indexed). */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

/** Wall-clock components of a UTC instant, as seen in `timezone`. */
interface ZonedParts {
  year: number
  month: number   // 0-indexed
  day: number
  hour: number
  minute: number
  second: number
  ms: number
}

function toZonedParts(base: Date, timezone: string): ZonedParts {
  const m = moment.tz(base, timezone)
  return {
    year: m.year(),
    month: m.month(),
    day: m.date(),
    hour: m.hour(),
    minute: m.minute(),
    second: m.second(),
    ms: m.millisecond(),
  }
}

/**
 * Compose a wall-time in `timezone` from `p` and convert back to UTC.
 * This is what makes weekly/monthly expansion DST-safe: a slot booked at
 * 1pm ET stays at 1pm ET even when the occurrence lands in a different
 * DST regime than the anchor.
 *
 * `Date.UTC` normalizes month/day overflow (e.g. Oct 37 → Nov 6) — the
 * weekly branch relies on this to add N * step days without pre-normalizing.
 */
function fromZonedParts(p: ZonedParts, timezone: string): Date {
  const normalized = new Date(Date.UTC(p.year, p.month, p.day, p.hour, p.minute, p.second, p.ms))
  // moment.tz with an array of parts interprets them as wall-time in `timezone`.
  return moment.tz(
    [
      normalized.getUTCFullYear(),
      normalized.getUTCMonth(),
      normalized.getUTCDate(),
      normalized.getUTCHours(),
      normalized.getUTCMinutes(),
      normalized.getUTCSeconds(),
      normalized.getUTCMilliseconds(),
    ],
    timezone,
  ).toDate()
}

/** Nth-weekday-of-month day-of-month, with 5th-week fallback to the last occurrence. */
function nthWeekdayDayOfMonth(year: number, month: number, weekday: number, position: number): number {
  const firstOfMonth = new Date(Date.UTC(year, month, 1))
  const firstWeekday = firstOfMonth.getUTCDay()
  const offsetToWeekday = (weekday - firstWeekday + 7) % 7
  const targetDay = 1 + offsetToWeekday + (position - 1) * 7
  const maxDay = lastDayOfMonth(year, month)
  return targetDay > maxDay ? targetDay - 7 : targetDay
}

/**
 * Clone `base` with its UTC year/month swapped to `(year, month)` and the
 * day-of-month clamped to that month's last day if it would overflow (e.g.
 * Jan 31 → Feb 28/29). Preserves the original hours/minutes/seconds.
 */
function moveToMonth(base: Date, year: number, month: number): Date {
  const day = Math.min(base.getUTCDate(), lastDayOfMonth(year, month))
  return new Date(Date.UTC(
    year, month, day,
    base.getUTCHours(), base.getUTCMinutes(), base.getUTCSeconds(), base.getUTCMilliseconds(),
  ))
}

/**
 * Position of `date` within its month, by weekday. Example: Mar 10 2026 is
 * a Tuesday and the 2nd Tuesday of the month → position 2. Dates in the
 * last 7 days of the month are also reported as position 5 OR as a "last"
 * hint — we keep both so the caller can decide how to interpret a missing
 * 5th occurrence.
 */
function weekdayPosition(date: Date): { weekday: number; position: 1 | 2 | 3 | 4 | 5 } {
  const weekday = date.getUTCDay()
  const position = Math.ceil(date.getUTCDate() / 7) as 1 | 2 | 3 | 4 | 5
  return { weekday, position }
}

/**
 * Find the date whose UTC year/month is `(year, month)` that falls on
 * `weekday` at the given occurrence `position` (1..5). If that occurrence
 * doesn't exist in the target month (e.g. no 5th Tuesday), fall back to
 * the LAST occurrence of `weekday` in the month. Preserves hours/etc from `base`.
 */
function nthWeekdayInMonth(base: Date, year: number, month: number, weekday: number, position: number): Date {
  const firstOfMonth = new Date(Date.UTC(year, month, 1))
  const firstWeekday = firstOfMonth.getUTCDay()
  const offsetToWeekday = (weekday - firstWeekday + 7) % 7
  const targetDay = 1 + offsetToWeekday + (position - 1) * 7
  const maxDay = lastDayOfMonth(year, month)

  const day = targetDay > maxDay
    // Fall back to the LAST occurrence of the weekday in the month.
    ? targetDay - 7
    : targetDay

  return new Date(Date.UTC(
    year, month, day,
    base.getUTCHours(), base.getUTCMinutes(), base.getUTCSeconds(), base.getUTCMilliseconds(),
  ))
}

/**
 * Expand a single start date into the full list of occurrence starts
 * described by `spec`. Result length = `min(spec.count, MAX_RECURRENCE_COUNT)`,
 * always at least 1.
 *
 * Pass `timezone` (IANA) to preserve wall-clock intent across DST boundaries.
 * Without it, math is naive UTC — an anchor booked pre-DST expanded to a
 * post-DST occurrence drifts an hour. All new callers should pass timezone.
 */
export function expandRecurrence(
  base: Date,
  spec: RecurrenceSpec,
  maxCount: number = MAX_RECURRENCE_COUNT,
  timezone?: string,
): Date[] {
  const count = Math.max(1, Math.min(maxCount, Math.floor(spec.count || 1)))
  const out: Date[] = []

  if (spec.pattern === 'weekly' || spec.pattern === 'biweekly') {
    const step = spec.pattern === 'weekly' ? 7 : 14
    if (!timezone) {
      for (let i = 0; i < count; i++) {
        out.push(new Date(base.getTime() + i * step * 24 * 60 * 60 * 1000))
      }
      return out
    }
    const zp = toZonedParts(base, timezone)
    for (let i = 0; i < count; i++) {
      out.push(fromZonedParts({ ...zp, day: zp.day + i * step }, timezone))
    }
    return out
  }

  if (spec.pattern === 'monthly') {
    const mode: MonthlyMode = spec.monthlyMode ?? 'date'
    if (mode === 'date') {
      if (!timezone) {
        const y0 = base.getUTCFullYear()
        const m0 = base.getUTCMonth()
        for (let i = 0; i < count; i++) {
          out.push(moveToMonth(base, y0, m0 + i))
        }
        return out
      }
      const zp = toZonedParts(base, timezone)
      for (let i = 0; i < count; i++) {
        const total = zp.month + i
        const year = zp.year + Math.floor(total / 12)
        const month = ((total % 12) + 12) % 12
        const day = Math.min(zp.day, lastDayOfMonth(year, month))
        out.push(fromZonedParts({ ...zp, year, month, day }, timezone))
      }
      return out
    }
    // mode === 'weekday'
    if (!timezone) {
      const { weekday, position } = weekdayPosition(base)
      const y0 = base.getUTCFullYear()
      const m0 = base.getUTCMonth()
      for (let i = 0; i < count; i++) {
        const total = m0 + i
        const year = y0 + Math.floor(total / 12)
        const month = ((total % 12) + 12) % 12
        out.push(nthWeekdayInMonth(base, year, month, weekday, position))
      }
      return out
    }
    const zp = toZonedParts(base, timezone)
    const weekday = new Date(Date.UTC(zp.year, zp.month, zp.day)).getUTCDay()
    const position = Math.ceil(zp.day / 7)
    for (let i = 0; i < count; i++) {
      const total = zp.month + i
      const year = zp.year + Math.floor(total / 12)
      const month = ((total % 12) + 12) % 12
      const day = nthWeekdayDayOfMonth(year, month, weekday, position)
      out.push(fromZonedParts({ ...zp, year, month, day }, timezone))
    }
    return out
  }

  // Fallback (shouldn't hit with typed inputs)
  return [base]
}

// ─── Series materialization ───────────────────────────────────────────────
//
// A `SupervisionSeries` row is the recurring template; the actual bookable
// slots are the N Supervision rows we materialize from it. `materializeSeries`
// turns the series spec (with DB-flavored uppercase enums) into the list of
// (start, end) tuples to insert. Reuses `expandRecurrence` under the hood so
// modal preview + server insert always agree.

/** DB enum for `supervision_series.pattern`. */
export type SeriesPattern = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'
/** DB enum for `supervision_series.monthlyMode`. NULL when pattern !== MONTHLY. */
export type SeriesMonthlyMode = 'DATE_OF_MONTH' | 'NTH_WEEKDAY'

export interface SeriesSpec {
  pattern: SeriesPattern
  monthlyMode?: SeriesMonthlyMode | null
  occurrenceCount: number
  /** First occurrence — UTC. */
  anchorStart: Date
  /** First occurrence's end — UTC. Slot duration is derived from this. */
  anchorEnd: Date
  /** Optional hard stop — any occurrence past this is dropped. */
  endsAt?: Date | null
  /** IANA timezone the series is anchored in. Required for DST-safe expansion. */
  timezone?: string
}

export interface SeriesOccurrence {
  start: Date
  end: Date
}

const SERIES_TO_LEGACY_PATTERN: Record<SeriesPattern, Exclude<RecurrencePattern, 'none'>> = {
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
}
const SERIES_TO_LEGACY_MONTHLY: Record<SeriesMonthlyMode, MonthlyMode> = {
  DATE_OF_MONTH: 'date',
  NTH_WEEKDAY: 'weekday',
}

/**
 * Expand a `SupervisionSeries` spec into the concrete (start, end) tuples
 * that should be inserted as `Supervision` rows. Respects `endsAt` and the
 * series-level cap. Slot duration is (anchorEnd - anchorStart) — every
 * occurrence keeps that same length.
 */
export function materializeSeries(spec: SeriesSpec): SeriesOccurrence[] {
  const legacyPattern = SERIES_TO_LEGACY_PATTERN[spec.pattern]
  const legacyMonthlyMode = spec.pattern === 'MONTHLY'
    ? SERIES_TO_LEGACY_MONTHLY[spec.monthlyMode ?? 'DATE_OF_MONTH']
    : undefined

  const starts = expandRecurrence(
    spec.anchorStart,
    { pattern: legacyPattern, count: spec.occurrenceCount, monthlyMode: legacyMonthlyMode },
    MAX_SERIES_OCCURRENCE_COUNT,
    spec.timezone,
  )
  const durationMs = spec.anchorEnd.getTime() - spec.anchorStart.getTime()
  const occurrences: SeriesOccurrence[] = starts.map(start => ({
    start,
    end: new Date(start.getTime() + durationMs),
  }))

  if (spec.endsAt) {
    const endsAtMs = spec.endsAt.getTime()
    return occurrences.filter(o => o.start.getTime() <= endsAtMs)
  }
  return occurrences
}

/**
 * Human-readable description of a monthly weekday position, using the given
 * date to derive the weekday and position. Example: passing a date that is
 * the 2nd Tuesday → "2nd Tuesday". Used in the modal's radio label so the
 * supervisor sees what "weekday" mode means for their chosen start date.
 */
export function describeMonthlyWeekday(date: Date): string {
  const { weekday, position } = weekdayPosition(date)
  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const ordinals: Record<number, string> = {
    1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th',
  }
  return `${ordinals[position] || `${position}th`} ${weekdayNames[weekday]}`
}
